import type { ScalarStatKey } from '../types/buff.types';

export type StatFormat = 'int' | 'percent' | 'multiplier' | 'perSecond';

export interface StatDescriptor {
  key: ScalarStatKey;
  label: string;
  description: string;
  format: StatFormat;
}

/**
 * Player-facing stat panel (in-run equipment screen). Curated subset of
 * `IEntityStats` — internal/visual fields (opacity, size) are intentionally
 * left out. Each entry pairs a stat with a one-line effect description.
 */
export const PLAYER_STAT_DESCRIPTORS: readonly StatDescriptor[] = [
  {
    key: 'maxLife',
    label: 'Vie max',
    description: 'Points de vie maximum. À zéro, vous êtes vaincu.',
    format: 'int',
  },
  {
    key: 'maxShield',
    label: 'Bouclier max',
    description: 'Absorbe les dégâts avant les points de vie.',
    format: 'int',
  },
  {
    key: 'physicalAttack',
    label: 'Attaque physique',
    description: 'Augmente les dégâts des sorts et attaques physiques.',
    format: 'int',
  },
  {
    key: 'magicAttack',
    label: 'Attaque magique',
    description: 'Augmente les dégâts des sorts magiques.',
    format: 'int',
  },
  {
    key: 'physicalDefense',
    label: 'Défense physique',
    description: 'Réduit les dégâts physiques subis.',
    format: 'int',
  },
  {
    key: 'magicDefense',
    label: 'Défense magique',
    description: 'Réduit les dégâts magiques subis.',
    format: 'int',
  },
  {
    key: 'critChancePhysical',
    label: 'Crit. physique',
    description: "Chance qu'une attaque physique inflige un coup critique.",
    format: 'percent',
  },
  {
    key: 'critChanceMagic',
    label: 'Crit. magique',
    description: "Chance qu'un sort magique inflige un coup critique.",
    format: 'percent',
  },
  {
    key: 'critMultiplier',
    label: 'Mult. critique',
    description: 'Multiplicateur de dégâts appliqué sur un coup critique.',
    format: 'multiplier',
  },
  {
    key: 'moveSpeed',
    label: 'Vitesse',
    description: 'Vitesse de déplacement (100 % = vitesse normale).',
    format: 'percent',
  },
  {
    key: 'spellCastTime',
    label: 'Temps de cast',
    description:
      'Multiplie la durée de lancement des sorts (plus bas = plus rapide).',
    format: 'multiplier',
  },
  {
    key: 'spellCooldown',
    label: 'Recharge des sorts',
    description:
      'Multiplie le temps de recharge des sorts (plus bas = plus court).',
    format: 'multiplier',
  },
  {
    key: 'viewRange',
    label: 'Vision',
    description: 'Rayon de découverte du brouillard de guerre.',
    format: 'int',
  },
  {
    key: 'pickupReach',
    label: 'Portée de ramassage',
    description: 'Distance de ramassage automatique des objets au sol.',
    format: 'int',
  },
];

export function formatStatValue(value: number, format: StatFormat): string {
  switch (format) {
    case 'percent':
      return `${Math.round(value)} %`;
    case 'multiplier':
      return `×${Math.round(value * 100) / 100}`;
    case 'perSecond':
      return `${Math.round(value * 10) / 10} /s`;
    case 'int':
    default:
      return `${Math.round(value)}`;
  }
}
